/**
 * ChainAI Operations Dashboard.
 *
 * Reads from GET /api/ai/ops-dashboard, which aggregates the same read-only
 * tool outputs the chat uses, then renders 4 widgets (Labor, Kitchen, Kiosks,
 * Customer Recovery) + a top-of-page sales tile. Phase 2 also includes a
 * two-step SMS campaign sender wired into the Customer Recovery widget.
 */
import React, { useEffect, useState } from 'react';
import {
  Sparkles, Users2, ChefHat, MonitorSmartphone, UserCheck, AlertCircle, Loader2,
} from 'lucide-react';

import { API_BASE_URL } from '@/config/api';
import { previewCampaign, sendCampaign } from '@/api/aiApi';

function MetricTile({ label, value, sub, tone = 'default' }) {
  const toneClass =
    tone === 'warn' ? 'border-amber-300 bg-amber-50' :
    tone === 'bad' ? 'border-rose-300 bg-rose-50' :
    'border-zinc-200 bg-white';
  return (
    <div className={`rounded-xl border ${toneClass} p-4 shadow-sm`}>
      <div className="text-xs uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 tabular-nums">{value}</div>
      {sub ? <div className="mt-1 text-xs text-zinc-500">{sub}</div> : null}
    </div>
  );
}

function Widget({ icon: Icon, title, available, message, children, action }) {
  return (
    <section
      className="flex flex-col gap-3 rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm"
      data-testid={`ops-widget-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-amber-500" />
        <h3 className="font-semibold tracking-tight">{title}</h3>
        {action ? <div className="ml-auto">{action}</div> : null}
      </div>
      {available === false ? (
        <div className="rounded-xl border border-zinc-100 bg-zinc-50 p-3 text-sm italic text-zinc-500">
          {message || 'Data not tracked yet.'}
        </div>
      ) : (
        children
      )}
    </section>
  );
}

export default function ChainAiOpsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Campaign send modal state
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [previewMessage, setPreviewMessage] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_BASE_URL}/api/ai/ops-dashboard`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        setError(`Failed to load dashboard (${res.status}).`);
        setData(null);
      } else {
        setData(await res.json());
      }
    } catch (e) {
      setError('Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handlePreview = async () => {
    setPreviewing(true);
    setSendResult(null);
    try {
      const p = await previewCampaign({});
      setPreview(p);
      setPreviewMessage(p?.data?.message || '');
    } catch (e) {
      setError('Failed to generate campaign preview.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async (dryRun) => {
    setSending(true);
    setSendResult(null);
    try {
      const r = await sendCampaign({ message: previewMessage, dry_run: dryRun });
      setSendResult(r);
      // Reload dashboard so recovery widget reflects post-send state.
      await load();
    } catch (e) {
      setSendResult({ success: false, error: 'Send failed' });
    } finally {
      setSending(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex h-96 items-center justify-center text-zinc-500" data-testid="ops-loading">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="ml-2">Loading operations dashboard…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="ops-error">
        <AlertCircle className="mr-2 inline h-4 w-4" />
        {error}
      </div>
    );
  }

  const sales = data?.sales_today;
  const labor = data?.labor;
  const kitchen = data?.kitchen;
  const kiosks = data?.kiosks;
  const recovery = data?.customer_recovery;

  const laborPct = labor?.available ? labor.data?.labor_cost_pct : null;
  const laborTone = laborPct === null || laborPct === undefined
    ? 'default'
    : laborPct > 35 ? 'bad' : laborPct < 18 ? 'warn' : 'default';

  const kitchenAvg = kitchen?.available ? kitchen.data?.avg_minutes : null;
  const kitchenTone = kitchenAvg === null || kitchenAvg === undefined
    ? 'default' : kitchenAvg > 25 ? 'bad' : kitchenAvg > 20 ? 'warn' : 'default';

  const offlineKiosks = kiosks?.data?.offline_count ?? 0;
  const kioskTone = offlineKiosks > 0 ? 'warn' : 'default';

  const recoveryCount = recovery?.available ? (recovery.data?.count ?? 0) : 0;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6" data-testid="chainai-ops-dashboard">
      <header className="mb-6 flex items-center gap-2">
        <Sparkles className="h-6 w-6 text-amber-500" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">ChainAI Operations</h1>
          <p className="text-sm text-zinc-500">
            Live read-only monitor. Refreshes per page-load. Timezone: <code className="rounded bg-zinc-100 px-1">{data?.timezone || 'UTC'}</code>.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="ml-auto rounded-xl border border-zinc-200 px-3 py-1.5 text-sm hover:bg-zinc-50"
          data-testid="ops-refresh"
        >
          Refresh
        </button>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricTile
          label="Sales today"
          value={sales?.available
            ? `$${(sales.data?.net_sales ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
            : '—'}
          sub={sales?.available ? `${sales.data?.paid_transactions ?? 0} paid orders` : 'Unavailable'}
        />
        <MetricTile
          label="Labor cost %"
          value={laborPct !== null && laborPct !== undefined ? `${laborPct}%` : '—'}
          sub={labor?.available ? `${Math.round(labor.data?.hours_worked ?? 0)}h worked (last 7d)` : (labor?.message || 'Unavailable')}
          tone={laborTone}
        />
        <MetricTile
          label="Avg ticket time"
          value={kitchenAvg !== null && kitchenAvg !== undefined ? `${kitchenAvg} min` : '—'}
          sub={kitchen?.available ? `${kitchen.data?.tickets ?? 0} tickets (last 24h)` : (kitchen?.message || 'Unavailable')}
          tone={kitchenTone}
        />
        <MetricTile
          label="Kiosks"
          value={kiosks?.available ? `${kiosks.data?.online_count ?? 0}/${(kiosks.data?.online_count ?? 0) + offlineKiosks}` : '—'}
          sub={offlineKiosks > 0 ? `${offlineKiosks} offline` : 'All online'}
          tone={kioskTone}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Widget icon={Users2} title="Labor" available={labor?.available} message={labor?.message}>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-500">Labor cost</span>
              <span className="font-medium tabular-nums">${(labor?.data?.labor_cost ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Sales</span>
              <span className="font-medium tabular-nums">${(labor?.data?.sales ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Shifts</span>
              <span className="font-medium tabular-nums">{labor?.data?.shifts ?? 0}</span>
            </div>
            {(labor?.data?.suggestions || []).length > 0 ? (
              <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                {labor.data.suggestions.join(' ')}
              </div>
            ) : null}
          </div>
        </Widget>

        <Widget icon={ChefHat} title="Kitchen" available={kitchen?.available} message={kitchen?.message}>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-500">Average ticket time</span>
              <span className="font-medium tabular-nums">{kitchenAvg ?? '—'} min</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Slow (&gt;25m)</span>
              <span className="font-medium tabular-nums">
                {(kitchen?.data?.slow_orders ?? 0)} ({kitchen?.data?.slow_pct ?? 0}%)
              </span>
            </div>
            {kitchen?.data?.p95_minutes ? (
              <div className="flex justify-between">
                <span className="text-zinc-500">P95</span>
                <span className="font-medium tabular-nums">{kitchen.data.p95_minutes} min</span>
              </div>
            ) : null}
            {(kitchen?.data?.issues || []).length > 0 ? (
              <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50 p-2 text-xs text-rose-900">
                {kitchen.data.issues.join(' ')}
              </div>
            ) : null}
          </div>
        </Widget>

        <Widget icon={MonitorSmartphone} title="Kiosks" available={kiosks?.available} message={kiosks?.message}>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-500">Online</span>
              <span className="font-medium tabular-nums">{kiosks?.data?.online_count ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Offline</span>
              <span className={`font-medium tabular-nums ${offlineKiosks > 0 ? 'text-rose-700' : ''}`}>
                {offlineKiosks}
              </span>
            </div>
            {(kiosks?.data?.offline_kiosks || []).map((k) => (
              <div key={k.id} className="flex justify-between rounded-lg bg-rose-50 px-2 py-1 text-xs text-rose-900">
                <span>{k.name}</span>
                <span>last seen {k.last_heartbeat ? new Date(k.last_heartbeat).toLocaleTimeString() : 'never'}</span>
              </div>
            ))}
            <div className="mt-2 text-xs text-zinc-500 italic">
              Printer health is reported on-device only; no backend signal yet.
            </div>
          </div>
        </Widget>

        <Widget
          icon={UserCheck}
          title="Customer Recovery"
          available={recovery?.available}
          message={recovery?.message}
          action={
            <button
              type="button"
              onClick={handlePreview}
              disabled={previewing || recoveryCount === 0}
              className="rounded-xl bg-amber-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-amber-600 disabled:cursor-not-allowed disabled:bg-zinc-300"
              data-testid="ops-campaign-preview-btn"
            >
              {previewing ? 'Previewing…' : 'Preview SMS campaign'}
            </button>
          }
        >
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-500">Inactive candidates</span>
              <span className="font-medium tabular-nums">{recoveryCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Inactive ≥</span>
              <span className="font-medium tabular-nums">{recovery?.data?.inactive_days_threshold ?? 60} days</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Min past orders</span>
              <span className="font-medium tabular-nums">{recovery?.data?.min_past_orders ?? 2}</span>
            </div>
          </div>
        </Widget>
      </div>

      {/* Two-step send modal */}
      {preview ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50 p-4" data-testid="ops-campaign-modal">
          <div className="w-full max-w-lg space-y-4 rounded-2xl bg-white p-6 shadow-xl">
            <header className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-500" />
              <h3 className="text-lg font-semibold">SMS Campaign Preview</h3>
            </header>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-zinc-500">Recipients</span><span className="font-medium" data-testid="ops-modal-recipients">{preview.data.recipient_count}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Segments / SMS</span><span className="font-medium">{preview.data.segments}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Estimated cost</span><span className="font-medium">${preview.data.estimated_cost_usd}</span></div>
            </div>
            <label className="block text-xs uppercase tracking-wide text-zinc-500">Message</label>
            <textarea
              rows={3}
              value={previewMessage}
              onChange={(e) => setPreviewMessage(e.target.value)}
              maxLength={320}
              className="w-full rounded-xl border border-zinc-200 p-3 text-sm"
              data-testid="ops-modal-message"
            />
            {sendResult ? (
              <div className={`rounded-xl border p-3 text-sm ${sendResult.success ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-rose-200 bg-rose-50 text-rose-900'}`} data-testid="ops-send-result">
                {sendResult.dry_run ? 'Dry-run complete: ' : 'Send complete: '}
                {sendResult.sent_count} sent · {sendResult.failed_count} failed · {sendResult.opted_out_skipped} opted-out skipped
                {sendResult.quota_blocked ? ' (daily quota reached)' : ''}
              </div>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => { setPreview(null); setSendResult(null); }}
                className="rounded-xl border border-zinc-200 px-3 py-2 text-sm hover:bg-zinc-50"
                data-testid="ops-modal-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={sending}
                onClick={() => handleSend(true)}
                className="rounded-xl border border-zinc-300 px-3 py-2 text-sm hover:bg-zinc-50 disabled:opacity-50"
                data-testid="ops-modal-dry-run"
              >
                {sending ? 'Working…' : 'Dry run'}
              </button>
              <button
                type="button"
                disabled={sending || preview.data.recipient_count === 0}
                onClick={() => handleSend(false)}
                className="ml-auto rounded-xl bg-amber-500 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-amber-600 disabled:cursor-not-allowed disabled:bg-zinc-300"
                data-testid="ops-modal-send"
              >
                {sending ? 'Sending…' : `Send ${preview.data.recipient_count} SMS`}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
